import * as React from 'react'
import { AlertTriangle } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { FormField } from '@/components/templates/entity-form'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type {
  CustomField,
  CustomFieldConversionPreview,
  CustomFieldType,
} from '@/lib/contacts/types'

const customFieldTypes: CustomFieldType[] = [
  'text',
  'multiline text',
  'number',
  'date',
  'checkbox',
  'single-select',
  'multi-select',
  'URL',
  'file',
]

export function ConvertTypeDialog({
  field,
  preview,
  pending,
  error,
  onClose,
  onPreview,
  onConfirm,
}: {
  field: CustomField
  preview: CustomFieldConversionPreview | null
  pending: boolean
  error: Error | null
  onClose: () => void
  onPreview: (fieldType: CustomFieldType) => void
  onConfirm: (fieldType: CustomFieldType) => void
}) {
  const [fieldType, setFieldType] = React.useState<CustomFieldType>(() => nextFieldType(field))
  const fieldTypeError = apiFieldMessages(error, 'field_type')[0]

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Convert {field.label}</DialogTitle>
          <DialogDescription>{field.field_type} · Custom Field type</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {fieldTypeError && <Alert>{fieldTypeError}</Alert>}
          <FormField label="New type" htmlFor="custom-field-new-type">
            <Select
              value={fieldType}
              onValueChange={(value) => setFieldType(value as CustomFieldType)}
            >
              <SelectTrigger id="custom-field-new-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {customFieldTypes
                  .filter((candidate) => candidate !== field.field_type)
                  .map((candidate) => (
                    <SelectItem key={candidate} value={candidate}>
                      {candidate}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </FormField>

          {preview && <ConversionPreview preview={preview} />}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => onPreview(fieldType)}
          >
            Preview conversion
          </Button>
          {preview && (
            <Button
              type="button"
              variant="destructive"
              disabled={pending}
              onClick={() => onConfirm(fieldType)}
            >
              Convert and apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ConversionPreview({ preview }: { preview: CustomFieldConversionPreview }) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="pb-2 text-caption uppercase tracking-wide text-muted-foreground">
          Existing values -&gt; converted values
        </p>
        <div className="overflow-hidden rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Current value</TableHead>
                <TableHead className="w-24">Contacts</TableHead>
                <TableHead>Becomes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.value_mappings.map((mapping) => (
                <TableRow key={`${mapping.value}-${mapping.becomes ?? 'cleared'}`}>
                  <TableCell>{mapping.value}</TableCell>
                  <TableCell className="text-muted-foreground">{mapping.contact_count}</TableCell>
                  <TableCell>
                    {mapping.cleared ? (
                      <Badge variant="destructive">Will be cleared</Badge>
                    ) : (
                      <Badge variant="neutral">{mapping.becomes}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      {preview.cleared_value_count > 0 && (
        <Alert variant="warning" className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            {preview.cleared_value_count} stored{' '}
            {preview.cleared_value_count === 1 ? 'value' : 'values'} will be cleared.
          </span>
        </Alert>
      )}
    </div>
  )
}

function nextFieldType(field: CustomField) {
  return customFieldTypes.find((fieldType) => fieldType !== field.field_type) ?? 'text'
}
